import { useCallback } from 'react';
import type { Chat } from '@trinity/chat';
import { ChatInstanceItemType, ChatResponseType } from '@trinity/chat';
import { FileUploadItem } from './Item.FileUpload';
import { KolSearchItem } from './Item.KolSearch';
import { QueryItem } from './Item.Query';
import { KolSearchContainer } from './KolSearch.Containers';

type Props = {
  canRegenerate: boolean;
  item: Chat.InstanceItem;
};

// Helper method for FRM demo modifications
const applyFRMDemoModifications = (profile: any, citations?: Chat.Citation[]) => {

  profile.keyInsights = [
    '44% of Eylea claim denials are linked to insufficient clinical documentation',
    'Average turnaround time (TAT) for Dr. Marlow\'s cases increased by 8%, indicating an opportunity to streamline processes',
    'Office staff reports confusion around Elevance\'s new PA criteria for Keytruda, suggesting a need for higher-touch support',
  ];

  profile.keyDiscussionQuestions = [
    'How willing is the office staff to adopt an Eylea PA Criteria Reference Card to ensure successful first approval?',
    'How much time is office staff spending on resubmitting claims where the diagnosis has been deemed non-covered?',
    'What feedback does office staff have on current Humana communication and support?',
  ];

  if (citations && Array.isArray(citations)) {
    const modifiedCitations = [...citations];

    modifiedCitations.forEach((citation, index) => {
      if (index === 2) {
        citation.title = 'MMIT Policy Reporter';
      } else if (index === 3) {
        citation.title = 'Clarivate';
      } else if (index === 4) {
        citation.title = 'Account Team Information';
      } else if (index === 5) {
        citation.title = 'Optum Specialty Pharmacy Data';
      } else if (citation.title === 'MSL Interactions') {
        citation.title = 'Interactions';
      }
    });

    const filteredCitations = modifiedCitations.filter((_citation, index) =>
      index !== 6 && index !== 8,
    );

    filteredCitations.forEach((citation, index) => {
      citation.ordinal = index + 1;
    });

    return filteredCitations;
  }

  return citations;
};

export const ChatItem = ({ canRegenerate, item }: Props) => {
  // Apply FRM demo modifications to query data
  const modifyHcpDataForFRMDemo = useCallback((queryData: Chat.InstanceQueryItem['query']) => {
    if (queryData?.response?.type === ChatResponseType.HcpSummary &&
      (queryData.response as any)?.data?.profile?.id === 15130806) {

      const modifiedQuery = JSON.parse(JSON.stringify(queryData)) as Chat.InstanceQueryItem['query'];
      const profile = (modifiedQuery.response as any).data.profile;

      const modifiedCitations = applyFRMDemoModifications(profile, modifiedQuery.citations);
      if (modifiedCitations) {
        modifiedQuery.citations = modifiedCitations;
      }

      return modifiedQuery;
    }

    return queryData;
  }, []);

  // Apply FRM demo modifications to KolSearch profile data
  const modifyKolSearchProfileForFRMDemo = useCallback((kolSearchData: Chat.KolSearchItem) => {
    if (kolSearchData?.profile?.id === 15130806) {

      const modifiedKolSearch = JSON.parse(JSON.stringify(kolSearchData)) as Chat.KolSearchItem;
      const profile = modifiedKolSearch.profile;

      if (profile) {
        const modifiedCitations = applyFRMDemoModifications(profile, modifiedKolSearch.citations);
        if (modifiedCitations) {
          modifiedKolSearch.citations = modifiedCitations;
        }
      }

      return modifiedKolSearch;
    }

    return kolSearchData;
  }, []);

  switch (item.type) {
    case ChatInstanceItemType.Query: {
      const modifiedItem = {
        ...item,
        query: modifyHcpDataForFRMDemo(item.query),
      };
      return (
        <QueryItem
          canRegenerate={canRegenerate}
          item={modifiedItem} />
      );
    }

    case ChatInstanceItemType.FileUpload:
      return <FileUploadItem item={item} />;

    case ChatInstanceItemType.KolSearch: {
      const modifiedItem = modifyKolSearchProfileForFRMDemo(item);
      return (
        <KolSearchContainer item={modifiedItem}>
          <KolSearchItem />
        </KolSearchContainer>
      );
    }

    default:
      return null;
    // throw new UnreachableCaseError(item);
  }
};